import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";

import myConfig from "../../configs/config";
import { getMapOptions } from "../PendingSchedules/rumoPistaUtils";
import {
  buildHeatmapWeightedPoints,
  DEFAULT_RAIN_MAP_ZOOM,
  formatRainMm,
  shouldUseCloseRainZoom,
} from "../../utils/rainHeatmapUtils";
import {
  clearRainHeatmapCircles,
  renderRainHeatmapCircles,
} from "../../utils/rainHeatmapOverlay";
import { LAND_PLOT_MAP_STYLE } from "../../utils/mapMeasurements";
import {
  applyViewportToGoogleMap,
  normalizeViewport,
} from "../../utils/mapViewport";

function fitMapToRainArea(map, maps, ring, gauges, padding = 48) {
  if (!map || !maps) return;

  const points = (gauges || []).filter(
    (gauge) =>
      Number.isFinite(Number(gauge.lat)) && Number.isFinite(Number(gauge.lng))
  );
  const area = points.length ? points : Array.isArray(ring) ? ring : [];
  if (!area.length) return;

  if (area.length === 1 || shouldUseCloseRainZoom(area)) {
    const lat =
      area.reduce((sum, point) => sum + Number(point.lat), 0) / area.length;
    const lng =
      area.reduce((sum, point) => sum + Number(point.lng), 0) / area.length;
    map.setCenter({ lat, lng });
    map.setZoom(DEFAULT_RAIN_MAP_ZOOM);
    return;
  }

  const bounds = new maps.LatLngBounds();
  area.forEach((point) => {
    bounds.extend(new maps.LatLng(point.lat, point.lng));
  });
  map.fitBounds(bounds, padding);
}

function refreshMapSize(map, maps) {
  if (!map || !maps) return;
  if (maps.event) {
    maps.event.trigger(map, "resize");
  }
}

function RainGaugeMarker({ label, mm, onSelect, selected }) {
  const className = selected
    ? "farm-rain-gauge-marker farm-rain-gauge-marker--button farm-rain-gauge-marker--selected"
    : "farm-rain-gauge-marker farm-rain-gauge-marker--button";

  return (
    <button
      type="button"
      className={className}
      title={`${label}: ${formatRainMm(mm)} mm`}
      onClick={(event) => {
        event.stopPropagation();
        if (onSelect) onSelect();
      }}
    >
      <span className="farm-rain-gauge-marker__icon">☔</span>
      <span className="farm-rain-gauge-marker__label">{label}</span>
      <strong className="farm-rain-gauge-marker__mm">{formatRainMm(mm)} mm</strong>
    </button>
  );
}

function FarmRainHeatmapMap({
  boundaryRing,
  landPlots,
  gauges,
  center,
  zoom,
  savedViewport,
  mapHeight = 560,
  selectedGaugeId,
  onGaugeClick,
  onMapReady,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const heatmapRef = useRef([]);
  const boundaryPolygonRef = useRef(null);
  const plotPolygonsRef = useRef([]);
  const appliedViewRef = useRef(false);
  const [canRenderMap, setCanRenderMap] = useState(false);

  const heatPoints = useMemo(
    () => buildHeatmapWeightedPoints(boundaryRing, gauges),
    [boundaryRing, gauges]
  );

  const maxGaugeRainMm = useMemo(() => {
    const values = (gauges || []).map((gauge) => Number(gauge.mm) || 0);
    if (!values.length) return 0;
    return Math.max(...values, 0);
  }, [gauges]);

  const colorScaleMaxMm = useMemo(
    () => Math.max(maxGaugeRainMm, 1),
    [maxGaugeRainMm]
  );

  const clearPlotPolygons = useCallback(() => {
    plotPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    plotPolygonsRef.current = [];
  }, []);

  const renderStaticPolygons = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    if (boundaryPolygonRef.current) {
      boundaryPolygonRef.current.setMap(null);
      boundaryPolygonRef.current = null;
    }
    clearPlotPolygons();

    if (Array.isArray(boundaryRing) && boundaryRing.length >= 3) {
      boundaryPolygonRef.current = new maps.Polygon({
        paths: boundaryRing,
        map,
        strokeColor: "#1565C0",
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: "#1565C0",
        fillOpacity: 0.04,
        zIndex: 2,
        clickable: false,
      });
    }

    (landPlots || []).forEach((plot) => {
      if (!plot || !plot.geomap) return;
      try {
        const geometry = JSON.parse(plot.geomap);
        const ring = geometry.coordinates[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));
        if (ring.length < 3) return;
        plotPolygonsRef.current.push(
          new maps.Polygon({
            paths: ring,
            map,
            strokeColor: LAND_PLOT_MAP_STYLE.strokeColor,
            strokeOpacity: 0.55,
            strokeWeight: 1,
            fillColor: LAND_PLOT_MAP_STYLE.fillColor,
            fillOpacity: 0.08,
            zIndex: 1,
            clickable: false,
          })
        );
      } catch (error) {
        // Ignora talhão com geometria inválida no demo.
      }
    });
  }, [boundaryRing, landPlots, clearPlotPolygons]);

  const renderHeatmap = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps || !heatPoints.length) return;

    clearRainHeatmapCircles(heatmapRef.current);
    heatmapRef.current = renderRainHeatmapCircles(
      maps,
      map,
      heatPoints,
      colorScaleMaxMm
    );
  }, [heatPoints, colorScaleMaxMm]);

  const updateHeatmap = useCallback(() => {
    renderHeatmap();
  }, [renderHeatmap]);

  const scheduleHeatmapRender = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps || !maps.event) {
      window.setTimeout(() => renderHeatmap(), 200);
      return;
    }

    maps.event.addListenerOnce(map, "idle", () => {
      renderHeatmap();
    });
  }, [renderHeatmap]);

  useEffect(() => {
    updateHeatmap();
  }, [updateHeatmap]);

  useEffect(() => {
    renderStaticPolygons();
  }, [renderStaticPolygons]);

  useEffect(
    () => () => {
      if (heatmapRef.current.length) {
        clearRainHeatmapCircles(heatmapRef.current);
        heatmapRef.current = [];
      }
      if (boundaryPolygonRef.current) {
        boundaryPolygonRef.current.setMap(null);
      }
      clearPlotPolygons();
    },
    [clearPlotPolygons]
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const syncRenderState = () => {
      setCanRenderMap(node.offsetWidth > 0 && node.offsetHeight > 0);
    };

    syncRenderState();
    requestAnimationFrame(syncRenderState);

    if (typeof ResizeObserver === "undefined") {
      const timer = window.setTimeout(syncRenderState, 120);
      return () => window.clearTimeout(timer);
    }

    const observer = new ResizeObserver(syncRenderState);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const applyOpeningView = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    const stored = normalizeViewport(savedViewport);
    if (stored) {
      applyViewportToGoogleMap(map, stored);
      appliedViewRef.current = true;
      return;
    }

    if (appliedViewRef.current) return;
    fitMapToRainArea(map, maps, boundaryRing, gauges);
    appliedViewRef.current = true;
  }, [boundaryRing, gauges, savedViewport]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;
    refreshMapSize(map, maps);
    scheduleHeatmapRender();
  }, [boundaryRing, gauges, scheduleHeatmapRender]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;
    refreshMapSize(map, maps);
    applyOpeningView();
    scheduleHeatmapRender();
  }, [mapHeight, applyOpeningView, scheduleHeatmapRender]);

  const handleGoogleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    appliedViewRef.current = false;
    map.setMapTypeId(maps.MapTypeId.SATELLITE);
    refreshMapSize(map, maps);
    applyOpeningView();
    renderStaticPolygons();
    scheduleHeatmapRender();
    if (onMapReady) onMapReady(map);

    window.requestAnimationFrame(() => {
      refreshMapSize(map, maps);
      applyOpeningView();
      renderStaticPolygons();
      scheduleHeatmapRender();
    });
  };

  if (!center || center.lat == null || center.lng == null) {
    return null;
  }

  return (
    <div
      className="farm-rain-heatmap-map"
      ref={containerRef}
      style={{ height: mapHeight }}
    >
      {canRenderMap ? (
        <div className="farm-rain-heatmap-map__surface">
          <GoogleMapReact
            key="rain-heatmap-map"
            bootstrapURLKeys={{
              key: myConfig.GOOGLE_API_KEY,
              libraries: ["geometry"],
            }}
            defaultCenter={center}
            defaultZoom={zoom || DEFAULT_RAIN_MAP_ZOOM}
            options={getMapOptions}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={handleGoogleApiLoaded}
          >
            {(gauges || []).map((gauge) => (
              <RainGaugeMarker
                key={gauge.id}
                lat={gauge.lat}
                lng={gauge.lng}
                label={gauge.label}
                mm={gauge.mm}
                selected={selectedGaugeId === gauge.id}
                onSelect={
                  onGaugeClick ? () => onGaugeClick(gauge) : undefined
                }
              />
            ))}
          </GoogleMapReact>
        </div>
      ) : null}
      <div
        className="farm-rain-heatmap-legend"
        aria-label={`Legenda de chuva de 0 mm a ${formatRainMm(maxGaugeRainMm)} mm`}
      >
        <span>0 mm</span>
        <div className="farm-rain-heatmap-legend__bar" />
        <span>{formatRainMm(maxGaugeRainMm)} mm</span>
      </div>
    </div>
  );
}

export default FarmRainHeatmapMap;
